import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Play, Trash2, Eye, Mail, FolderKanban, FileText ,Loader2} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { api } from "../lib/api";
import { fmtRelative, templatesMeta } from "../lib/format";
import { toast } from "sonner";
import { Pause, PlayCircle } from "lucide-react";
import { useAuth } from "../lib/auth";
export default function Projects() {
  const { user } = useAuth();
  const isOrgMember = user?.role === "org_member";
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [runTarget, setRunTarget] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // P7 — tab state: "active" (default) | "drafts"
  const tab = searchParams.get("tab") === "drafts" ? "drafts" : "active";
  const setTab = (t) => {
    if (t === "active") {
      const sp = new URLSearchParams(searchParams);
      sp.delete("tab");
      setSearchParams(sp, { replace: true });
    } else {
      setSearchParams({ tab: t }, { replace: true });
    }
  };
  const nav = useNavigate();

  // const load = () => {
  //   setLoading(true);
  //   api.get(`/projects?status=${tab === "drafts" ? "draft" : "active"}`)
  //     .then((r) => setProjects(r.data.projects || []))
  //     .finally(() => setLoading(false));
  // };

  const load = useCallback(() => {
    setLoading(true);

    api.get(`/projects?status=${tab === "drafts" ? "draft" : "active"}`)
      .then((r) => setProjects(r.data.projects || []))
      .finally(() => setLoading(false));
  }, [tab]);

  // useEffect(() => { load(); }, [tab]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load])

  // const runNow = async (id) => {
  //   setBusy(id);
  //   toast.message("Pipeline running, this may take a minute.");
  //   try {
  //     const r = await api.post(`/projects/${id}/run`);
  //     if (r.data.ok) {
  //       toast.success(`Done, ${r.data.kept} new results kept`);
  //       load();
  //     } else {
  //       toast.error(r.data.error || "Pipeline failed");
  //     }
  //   } catch (e) {
  //     toast.error(e?.response?.data?.detail || "Pipeline failed");
  //   } finally {
  //     setBusy(null);
  //   }
  // };


  const hasRunningProject = projects.some((p) => p.is_running);

  useEffect(() => {
    if (!hasRunningProject) return;

    const timer = setInterval(() => {
      load();
    }, 5000);

    return () => clearInterval(timer);
  }, [hasRunningProject, load]);

  // const runNow = async (id) => {
  //   setBusy(id);

  //   try {
  //     const r = await api.post(`/projects/${id}/run`);

  //     if (r.data.success) {
  //       toast.success("Project started. Results will appear shortly.");

  //       // Refresh immediately so is_running=true comes from backend
  //       await load();
  //     } else {
  //       toast.error(r.data.detail || "Unable to start project");
  //     }
  //   } catch (e) {
  //     toast.error(e?.response?.data?.detail || "Unable to start project");
  //   } finally {
  //     // Only controls immediate button click state
  //     setBusy(null);
  //   }
  // };


  const runNow = async (id) => {
    setBusy(id);
    
    // Optimistically update the project's running state so it stays running
    // in the UI and triggers the background polling effect.
    setProjects((prev) => 
      prev.map(p => p.id === id ? { ...p, is_running: true } : p)
    );

    toast.message("Pipeline running, this may take a few minutes.");

    try {
      const r = await api.post(`/projects/${id}/run`);

      if (r.data.ok) {
        toast.success(`Done, ${r.data.kept} new results kept`);
      } else {
        toast.error(r.data.error || r.data.detail || "Please try again");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        "Please try again"
      );
    } finally {
      setBusy(null);
      load();
    }
  };

  const handleToggleProject = async (id, isActive) => {
    const next = !isActive;

    try {
      await api.patch(`/projects/${id}`, {
        is_active: next,
      });

      toast.success(
        next
          ? "Project resumed"
          : "Project paused — pipeline will skip it"
      );

      setProjects((prev) =>
        prev.map((project) =>
          project.id === id
            ? { ...project, is_active: next }
            : project
        )
      );

      // Update your local state here
    } catch (err) {
      toast.error("Could not update status");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/projects/${deleteTarget.id}`);
      toast.success("Project deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const confirmRun = () => {
    if (!runTarget) return;
    const projectId = runTarget.id;
    setRunTarget(null);
    runNow(projectId);
  };

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const totalPages = Math.max(1, Math.ceil(projects.length / PAGE_SIZE));

  const paginatedProjects = projects.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [tab]);

  return (
    <div className="p-8 lg:p-12" data-testid="projects-page">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="type-eyebrow text-[#64748B]">Configuration</div>
          <h1 className="type-h1 text-[#0F1F3D] mt-3">Projects</h1>
          <p className="type-body text-[#475569] mt-3 prose-measure">Each project defines what to watch and how to score relevance.</p>
        </div>
        {!isOrgMember && (
          <Link to="/app/projects/new" data-testid="new-project-button" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#0F1F3D] text-white type-body-sm font-medium hover:bg-[#172A4F]">
            <Plus className="h-4 w-4" /> New project
          </Link>
        )}
      </div>

      {/* P7 — Active / Drafts tabs */}
      <div className="mt-8 inline-flex bg-white border border-[#E2E8F0] rounded-md p-1" data-testid="projects-tabs">
        <button
          onClick={() => setTab("active")}
          data-testid="projects-tab-active"
          className={`px-4 py-1.5 rounded text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${tab === "active" ? "bg-[#0F1F3D] text-white" : "text-[#475569] hover:text-[#0F1F3D]"
            }`}
        >
          <FolderKanban className="h-3.5 w-3.5" />
          Active projects
        </button>
        <button
          onClick={() => setTab("drafts")}
          data-testid="projects-tab-drafts"
          className={`px-4 py-1.5 rounded text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${tab === "drafts" ? "bg-[#0F1F3D] text-white" : "text-[#475569] hover:text-[#0F1F3D]"
            }`}
        >
          <FileText className="h-3.5 w-3.5" />
          Drafts
        </button>
      </div>

      <div className="mt-6 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F8F9FA] type-eyebrow text-[#64748B]">
            <tr>
              <th className="text-left px-6 py-3.5">Project</th>
              <th className="text-left px-6 py-3.5">Templates</th>
              <th className="text-center px-6 py-3.5 whitespace-nowrap">Last 24hrs</th>
              <th className="text-right px-6 py-3.5 whitespace-nowrap">Last run</th>
              <th className="text-right px-6 py-3.5 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="5" className="px-6 py-12 text-center type-body text-[#94A3B8]">Loading</td></tr>}
            {!loading && projects.length === 0 && <tr><td colSpan="5" className="px-6 py-12 text-center type-body text-[#94A3B8]">No projects yet. Create one to start.</td></tr>}
            {paginatedProjects.map((p) => {
              const tids = p.template_ids || [];
              const tms = templatesMeta(tids);
              const sectors = p.sectors || [];
              const running = busy === p.id || p.is_running;
              return (
                <tr
                  key={p.id}
                  onClick={() => nav(`/app/projects/${p.id}`)}
                  data-testid={`projects-row-${p.id}`}
                  className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] cursor-pointer"
                >
                  <td className="px-6 py-4">
                    <div className="type-h4 text-[#0F1F3D]">{p.name}</div>
                    {sectors.length > 0 && <div className="type-caption text-[#64748B] mt-1">
                      {sectors.slice(0, 4).join(" / ")}
                      {sectors.length > 4 && (
                        <>
                          <br />
                          {sectors.slice(4).join(" / ")}
                        </>
                      )}
                    </div>}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1.5">
                      {tms.map((tm) => (
                        <span key={tm.id || tm.label} className="px-2 py-1 rounded-md type-eyebrow" style={{ background: tm.color + "15", color: tm.color }}>
                          {tm.label}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {Number(p.new_results_count || 0) > 0 ? (
                      <span
                        data-testid={`new-results-badge-${p.id}`}
                        title={`${p.new_results_count} new result${p.new_results_count === 1 ? "" : "s"} in the last 24h`}
                        className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#047857] type-eyebrow font-semibold"
                      >
                        +{p.new_results_count} new
                      </span>
                    ) : (
                      <span className="type-caption text-[#94A3B8]">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="type-caption text-[#64748B]">{fmtRelative(p.last_run_at)}</span>
                  </td>
                  <td className="px-6 py-4 min-w-[290px]" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                      {p.status !== "draft" && (
                      <button
                        onClick={() => nav(`/app/feed?project_id=${p.id}`)}
                        data-testid={`view-results-${p.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E2E8F0] text-[#0F1F3D] type-caption font-semibold hover:bg-[#F1F5F9]"
                      >
                        <Eye className="h-3 w-3" /> View results
                      </button>
                      )}
                      {!isOrgMember && p.status !== "draft" && (
                      <div className="relative group">
                        <button
                            onClick={() => setRunTarget(p)}
                            disabled={running}
                            className="
                              inline-flex items-center gap-1.5
                              px-3 py-1.5
                              rounded-md
                              bg-[#0F1F3D]
                              text-white
                              hover:bg-[#172A4F]
                              disabled:opacity-70
                              disabled:cursor-not-allowed
                              type-caption
                            "
                          >
                            {running ? (
                              <>
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Running...
                              </>
                            ) : (
                              <>
                                <Play className="h-3 w-3" />
                                Run now
                              </>
                            )}
                          </button>

                        {/* Tooltip */}
                        <div
                          className="
      absolute
      bottom-full
      left-1/2
      -translate-x-1/2
      mb-3
      whitespace-nowrap

      rounded-lg
      bg-[#0B1120]/95
      px-3
      py-2

      text-[12px]
      font-medium
      text-white

      shadow-xl
      backdrop-blur-md

      opacity-0
      scale-95
      invisible

      transition-all
      duration-200

      group-hover:opacity-100
      group-hover:visible
      group-hover:scale-100

      z-50
    "
                        >
                          Run this project now

                          {/* Arrow */}
                          <div
                            className="
        absolute
        top-full
        left-1/2
        -translate-x-1/2

        border-l-[6px]
        border-r-[6px]
        border-t-[6px]

        border-l-transparent
        border-r-transparent
        border-t-[#0B1120]
      "
                          />
                        </div>
                      </div>
                      )}
                      {!isOrgMember && p.status !== "draft" && (
                      <div className="relative group">
                        <button
                          onClick={() => handleToggleProject(p.id, p.is_active)}
                          aria-label={p.is_active ? "Pause project" : "Resume project"}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors"
                        >
                          {p.is_active ? (
                            <Pause className="h-4 w-4 text-[#0F1F3D]" />
                          ) : (
                            <PlayCircle className="h-4 w-4 text-[#0F1F3D]" />
                          )}
                        </button>

                        <div
                          className="
      absolute bottom-full left-1/2 -translate-x-1/2 mb-3
      rounded-lg bg-[#0B1120]/95 px-3 py-2
      text-[12px] text-white whitespace-nowrap
      opacity-0 invisible scale-95
      group-hover:opacity-100
      group-hover:visible
      group-hover:scale-100
      transition-all duration-200
      backdrop-blur-md shadow-xl z-50
    "
                        >
                          {p.is_active ? "Pause project" : "Resume project"}

                          <div
                            className="
        absolute top-full left-1/2 -translate-x-1/2
        border-l-[6px] border-r-[6px] border-t-[6px]
        border-l-transparent border-r-transparent
        border-t-[#0B1120]
      "
    />
  </div>
</div>
                      )}
                      {!isOrgMember && (
                        <button onClick={() => setDeleteTarget(p)} data-testid={`delete-project-${p.id}`}
                          aria-label={`Delete ${p.name}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-[#E2E8F0] text-[#B91C1C] hover:bg-[#FEF2F2]">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>


      </div>

      {projects.length > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3 py-4 border-t ">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-4 py-2 rounded-md border border-[#E2E8F0] text-sm font-medium text-[#475569] hover:text-[#0F1F3D] hover:border-[#0F1F3D] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <div className="type-caption text-[#64748B]">
            Page <span className="font-semibold text-[#0F1F3D]">{page}</span> of{" "}
            <span className="font-semibold text-[#0F1F3D]">{totalPages}</span>
          </div>

          <button
            type="button"
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="px-4 py-2 rounded-md border border-[#E2E8F0] text-sm font-medium text-[#475569] hover:text-[#0F1F3D] hover:border-[#0F1F3D] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{deleteTarget?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              All results for this project will be permanently deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="delete-confirm-button"
              className="bg-[#B91C1C] text-white hover:bg-[#991B1B]"
            >
              Delete project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!runTarget} onOpenChange={(open) => !open && setRunTarget(null)}>
        <AlertDialogContent data-testid="run-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Run &ldquo;{runTarget?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This will run the entire project pipeline and may take several minutes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="run-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRun} data-testid="run-confirm-button">
              Run project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
